import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { ArrowLeft, Mail, User, MessageSquare, Calendar, RefreshCw, Lock, LogOut } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Simple password - you can change this
const ADMIN_PASSWORD = 'admin123';

// Login component
const AdminLogin = ({ onLogin }) => {
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    setLoading(true);

    if (password === ADMIN_PASSWORD) {
      sessionStorage.setItem('adminAuth', 'true');
      toast({
        title: 'Success!',
        description: 'Login successful',
      });
      onLogin();
    } else {
      toast({
        title: 'Error',
        description: 'Incorrect password',
        variant: 'destructive',
      });
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-black flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <div className="bg-gradient-to-br from-gray-900 to-black rounded-3xl shadow-2xl shadow-blue-500/20 border border-gray-800 overflow-hidden">
          <div className="text-center space-y-2 p-8 bg-gradient-to-br from-gray-800/50 to-black">
            <div className="flex justify-center mb-4">
              <div className="bg-blue-600/20 p-4 rounded-full border border-blue-500/50">
                <Lock className="h-8 w-8 text-blue-500" />
              </div>
            </div>
            <h2 className="text-3xl font-bold text-white">Admin Access</h2>
            <p className="text-base text-gray-400">
              Enter password to continue
            </p>
          </div>
          <div className="p-8">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="password" className="text-base font-medium text-gray-300">
                  Password
                </Label>
                <Input
                  id="password"
                  name="password"
                  type="password"
                  placeholder="Enter admin password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="h-12 text-base bg-gray-900/50 border-gray-700 text-white placeholder:text-gray-500 focus:border-blue-500"
                  autoFocus
                />
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full h-12 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white text-base font-semibold transition-all duration-300 hover:scale-105 shadow-lg shadow-blue-500/50"
              >
                {loading ? 'Checking...' : 'Login'}
              </Button>
            </form>
            <div className="mt-6 text-center">
              <p className="text-sm text-gray-500">
                Default password: <span className="text-gray-400 font-mono">admin123</span>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Contact card component
const ContactCard = ({ contact }) => {
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="bg-gradient-to-br from-gray-900 to-black rounded-2xl shadow-lg shadow-blue-500/20 p-6 border border-gray-800 hover:border-blue-500/50 transition-all duration-300">
      <div className="space-y-4">
        <div className="flex items-center gap-2 text-gray-300">
          <User className="h-5 w-5 text-blue-500" />
          <span className="font-semibold text-white">{contact.name}</span>
        </div>
        
        <div className="flex items-center gap-2 text-gray-300">
          <Mail className="h-5 w-5 text-blue-500" />
          <a href={`mailto:${contact.email}`} className="hover:text-blue-400 transition-colors">
            {contact.email}
          </a>
        </div>
        
        <div className="flex items-start gap-2 text-gray-300">
          <MessageSquare className="h-5 w-5 text-blue-500 mt-1" />
          <p className="flex-1 text-gray-300 leading-relaxed">{contact.message}</p>
        </div>
        
        <div className="flex items-center gap-2 text-gray-500 text-sm border-t border-gray-800 pt-4">
          <Calendar className="h-4 w-4" />
          <span>{formatDate(contact.created_at)}</span>
        </div>
      </div>
    </div>
  );
};

// Main admin dashboard component
const AdminDashboard = () => {
  const [contacts, setContacts] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const navigate = useNavigate();

  const fetchData = async () => {
    try {
      setRefreshing(true);
      const [contactsRes, analyticsRes] = await Promise.all([
        axios.get(`${API}/contacts`),
        axios.get(`${API}/analytics`),
      ]);
      
      setContacts(contactsRes.data);
      setAnalytics(analyticsRes.data);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to fetch data',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem('adminAuth');
    window.location.reload();
  };

  useEffect(() => {
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="h-12 w-12 text-blue-500 animate-spin mx-auto mb-4" />
          <p className="text-gray-400 text-lg">Loading data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black">
      {/* Header */}
      <div className="bg-gradient-to-br from-gray-900 to-black border-b border-gray-800 sticky top-0 z-50 shadow-lg shadow-blue-500/10">
        <div className="container mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                onClick={() => navigate('/')}
                variant="outline"
                className="border-gray-700 text-gray-300 hover:text-white hover:border-blue-500"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Home
              </Button>
              <h1 className="text-2xl md:text-3xl font-bold text-white">Admin Dashboard</h1>
            </div>
            <div className="flex items-center gap-3">
              <Button
                onClick={fetchData}
                disabled={refreshing}
                className="bg-blue-600 hover:bg-blue-700"
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
              <Button
                onClick={handleLogout}
                variant="outline"
                className="border-red-700 text-red-400 hover:bg-red-900/20 hover:border-red-500"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        {/* Analytics Summary */}
        {analytics && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            <div className="bg-gradient-to-br from-blue-900/50 to-blue-800/30 rounded-2xl p-6 border border-blue-500/50 shadow-lg shadow-blue-500/20">
              <h3 className="text-lg font-semibold text-gray-300 mb-2">Total Page Visits</h3>
              <p className="text-4xl font-bold text-white">{analytics.total_visits}</p>
            </div>
            <div className="bg-gradient-to-br from-green-900/50 to-green-800/30 rounded-2xl p-6 border border-green-500/50 shadow-lg shadow-green-500/20">
              <h3 className="text-lg font-semibold text-gray-300 mb-2">Contact Submissions</h3>
              <p className="text-4xl font-bold text-white">{contacts.length}</p>
            </div>
          </div>
        )}

        {/* Contact Submissions */}
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-2">
            <MessageSquare className="h-6 w-6 text-blue-500" />
            Contact Form Submissions
          </h2>
          
          {contacts.length === 0 ? (
            <div className="bg-gradient-to-br from-gray-900 to-black rounded-2xl p-12 text-center border border-gray-800">
              <MessageSquare className="h-16 w-16 text-gray-600 mx-auto mb-4" />
              <p className="text-gray-400 text-lg">No contact submissions yet</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {contacts.map((contact) => (
                <ContactCard key={contact.id} contact={contact} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Main component with authentication check
const AdminPage = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    const auth = sessionStorage.getItem('adminAuth');
    if (auth === 'true') {
      setIsAuthenticated(true);
    }
  }, []);

  const handleLogin = () => {
    setIsAuthenticated(true);
  };

  if (!isAuthenticated) {
    return <AdminLogin onLogin={handleLogin} />;
  }

  return <AdminDashboard />;
};

export default AdminPage;
